import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  IconButton,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDesktopLayout } from "./layout/AppLayout";
import { isTelegramMiniApp } from "../utils/platform";
import EventDetailsSheet from "./events/EventDetailsSheet";
import EventTypeChips from "./events/EventTypeChips";
import {
  GROUP_COLORS,
  GROUP_ICONS,
  GROUP_TITLES,
  cleanValue,
  formatEventTime,
  formatMoney,
  getAttr,
  getWriteoffLabel,
  hasWriteoff,
  pluralize,
  sortByDateDesc,
} from "./events/eventMeta";
import { CARD_BG_BOTTOM, CARD_BG_TOP, CARD_LABEL_COLOR, MUTED_TEXT_COLOR, hexToRgb } from "../styles/tokens";

// Подписи, которые для отдельных типов событий отличаются от общих
const DELETED_ITEMS_LABELS = {
  sum: "Сумма удалённого",
  orderSumAfterDiscount: "Сумма заказа",
};

const LABEL_OVERRIDES_BY_TYPE = {
  deletedNewItems: DELETED_ITEMS_LABELS,
  deletedPrintedItems: DELETED_ITEMS_LABELS,
};

const getColumnLabel = (eventType, key, defaultLabel) =>
  LABEL_OVERRIDES_BY_TYPE[eventType]?.[key] || defaultLabel;

// 🖥️ Десктопная версия (≥ md, не Telegram): диалог по центру, таблица событий
const SUMMARY_COLOR = "#a1a1aa";
const CELL_TEXT_COLOR = "#d4d4d8";
const EMPTY_CELL_COLOR = "#52525b";
// Непрозрачный фон закреплённой шапки таблицы — в тон поверхности диалога
const TABLE_HEAD_BG = "#202023";
// Отступ диалога от краёв экрана: не наезжает на шапку сайта (64px)
const DIALOG_VERTICAL_GAP = 80;
// Ниже этой ширины колонка «Открытие заказа» скрывается
const HIDE_OPEN_TIME_BELOW = 1100;

const EMPTY = { empty: true };
const text = (value, extra) => (value === null ? EMPTY : { value, ...extra });

const attrColumn = (name, label, extra = {}) => ({
  key: name,
  label,
  has: (event) => getAttr(event, name) !== undefined,
  cell: (event) => text(cleanValue(getAttr(event, name))),
  ...extra,
});

// «Выполнил» выделяем, если это не официант; «Доп. авторизацию» — если это не «Выполнил»
const highlightIfDiffers = (name, otherName) => (event) => {
  const value = cleanValue(getAttr(event, name));
  const other = cleanValue(getAttr(event, otherName));
  return text(value, { highlight: value !== null && value !== other });
};

const buildColumns = (singleDay) => [
  {
    key: "date",
    label: "Время",
    numeric: true,
    align: "left",
    has: (event) => Boolean(event.date),
    cell: (event) => text(event.date ? formatEventTime(event.date, singleDay) : null),
  },
  attrColumn("orderNum", "№ заказа", { numeric: true }),
  attrColumn("dishes", "Блюда", { flex: "60%", minWidth: 180, clamp: true }),
  attrColumn("reason", "Причина удаления", { wrap: true, minWidth: 140, clamp: true }),
  {
    key: "withWriteoff",
    label: "Со списанием",
    has: hasWriteoff,
    cell: (event) => text(getWriteoffLabel(event)),
  },
  attrColumn("penalty", "Штраф", {
    numeric: true,
    cell: (event) => text(formatMoney(getAttr(event, "penalty"))),
  }),
  // Главная сумма события — акцентом категории
  attrColumn("sum", "Сумма", {
    numeric: true,
    cell: (event) => text(formatMoney(getAttr(event, "sum")), { accent: true }),
  }),
  attrColumn("orderSumAfterDiscount", "Сумма со скидкой", {
    numeric: true,
    cell: (event) => text(formatMoney(getAttr(event, "orderSumAfterDiscount"))),
  }),
  attrColumn("waiter", "Официант"),
  attrColumn("user", "Выполнил", { cell: highlightIfDiffers("user", "waiter") }),
  attrColumn("auth", "Доп. авторизация", { cell: highlightIfDiffers("auth", "user") }),
  attrColumn("comment", "Комментарий", { flex: "40%", minWidth: 140, tooltip: true }),
  attrColumn("openTime", "Открытие заказа", {
    numeric: true,
    align: "left",
    hideBelow: HIDE_OPEN_TIME_BELOW,
    cell: (event) => {
      const value = cleanValue(getAttr(event, "openTime"));
      return text(value === null ? null : formatEventTime(value, singleDay));
    },
  }),
];

// Размеры ячейки по типу колонки. Для flex-колонок maxWidth: 0 + width в % —
// они делят оставшееся место; minWidth не даёт им схлопнуться (тогда скролл внутри диалога)
const columnCellSx = (column) => ({
  px: 1.25,
  textAlign: column.align || (column.numeric ? "right" : "left"),
  whiteSpace: "nowrap",
  ...(column.numeric && { fontVariantNumeric: "tabular-nums" }),
  ...(column.flex && { width: column.flex, maxWidth: 0, minWidth: column.minWidth, whiteSpace: "normal" }),
  // Текст с переносом, но без доли ширины (например, «Причина удаления»)
  ...(column.wrap && { minWidth: column.minWidth, whiteSpace: "normal" }),
  ...(column.hideBelow && {
    [`@media (max-width: ${column.hideBelow - 0.05}px)`]: { display: "none" },
  }),
});

const renderCellContent = (column, cell, accent) => {
  if (cell.empty) {
    return (
      <Box component="span" sx={{ color: EMPTY_CELL_COLOR }}>
        —
      </Box>
    );
  }
  const { value } = cell;
  if (column.clamp) {
    return (
      <Box
        component="span"
        title={value}
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordBreak: "break-word",
          lineHeight: 1.35,
        }}
      >
        {value}
      </Box>
    );
  }
  if (column.tooltip) {
    return (
      <Tooltip title={<span style={{ whiteSpace: "pre-wrap" }}>{value}</span>} placement="top-start">
        <Box component="span" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value}
        </Box>
      </Tooltip>
    );
  }
  if (cell.accent) {
    return (
      <Box component="span" sx={{ color: accent, fontWeight: 700 }}>
        {value}
      </Box>
    );
  }
  if (cell.highlight) {
    return (
      <Box component="span" sx={{ color: "#ffffff", fontWeight: 600 }}>
        {value}
      </Box>
    );
  }
  return value;
};

const DesktopEventDetailsDialog = ({
  open,
  onClose,
  onExited,
  group,
  subtitle,
  events,
  eventTypes,
  activeTab,
  onTabChange,
  singleDay,
}) => {
  const accent = GROUP_COLORS[group] || "#64b5f6";
  const accentRgb = hexToRgb(accent);
  const GroupIcon = GROUP_ICONS[group];
  const title = GROUP_TITLES[group] || group;
  const summary = [subtitle, `${events.length} ${pluralize(events.length, "событие", "события", "событий")}`]
    .filter(Boolean)
    .join(" · ");

  const tabEvents = sortByDateDesc(events.filter((e) => e.type === activeTab));
  // Столбец показываем, только если атрибут есть хотя бы у одного события вкладки
  const columns = buildColumns(singleDay).filter((column) => tabEvents.some(column.has));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited }}
      maxWidth={false}
      aria-labelledby="event-dialog-title"
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        },
      }}
      PaperProps={{
        sx: {
          width: "calc(100% - 64px)",
          maxWidth: 1200,
          // Высота по содержимому, но не выше 85vh и не наезжая на шапку сайта
          maxHeight: `min(85vh, calc(100vh - ${DIALOG_VERTICAL_GAP * 2}px))`,
          m: 4,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: `radial-gradient(600px 220px at 50% 0%, rgba(${accentRgb},0.12), transparent 70%), linear-gradient(180deg, ${CARD_BG_TOP}, ${CARD_BG_BOTTOM})`,
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "16px",
          color: "#fff",
        },
      }}
    >
      {/* Шапка: иконка категории, название, «дата · ресторан · N событий» */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 3, pt: 2.5, pb: 1.5, flexShrink: 0 }}>
        {GroupIcon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "11px",
              backgroundColor: `rgba(${accentRgb},0.15)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GroupIcon sx={{ fontSize: 22, color: accent }} />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="event-dialog-title" sx={{ fontSize: "18px", fontWeight: 800, color: "#fff", lineHeight: 1.25 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: "14px", color: SUMMARY_COLOR }}>{summary}</Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Закрыть" sx={{ width: 44, height: 44, flexShrink: 0, color: CARD_LABEL_COLOR }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {events.length === 0 ? (
        <Typography sx={{ px: 3, py: 5, textAlign: "center", color: MUTED_TEXT_COLOR }}>
          За выбранный период событий нет
        </Typography>
      ) : (
        <>
          <EventTypeChips
            eventTypes={eventTypes}
            events={events}
            activeTab={activeTab}
            onChange={onTabChange}
            accent={accent}
            sx={{ px: 3, pb: 1.5 }}
          />

          {/* Прокручивается только тело таблицы; при нехватке ширины — горизонтальный скролл здесь же */}
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 1.75, pb: 1.5 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      sx={{
                        ...columnCellSx(column),
                        whiteSpace: "nowrap",
                        backgroundColor: TABLE_HEAD_BG,
                        color: MUTED_TEXT_COLOR,
                        fontSize: "13px",
                        fontWeight: 500,
                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      {getColumnLabel(activeTab, column.key, column.label)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tabEvents.map((event, index) => (
                  <TableRow
                    key={event.id || index}
                    sx={{ height: 44, "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" } }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        sx={{
                          ...columnCellSx(column),
                          color: CELL_TEXT_COLOR,
                          fontSize: "14px",
                          borderBottom: "1px solid rgba(255,255,255,0.06)",
                        }}
                      >
                        {renderCellContent(column, column.cell(event), accent)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Dialog>
  );
};

const EventDetailsDialog = ({
  showEventDetailsDialog,
  setShowEventDetailsDialog,
  selectedGroup,
  events = [],
  periodLabel = "",
  serverName = "",
  singleDay = false,
  onExited,
}) => {
  const eventTypes = [...new Set(events.map((e) => e.type))];
  const [activeTab, setActiveTab] = useState("");

  // При смене группы/данных — первая вкладка
  useEffect(() => {
    setActiveTab(eventTypes[0] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, events]);

  const handleClose = () => setShowEventDetailsDialog(false);

  // Десктоп (не Telegram) — диалог с таблицей
  const desktop = useDesktopLayout() && !isTelegramMiniApp();
  if (desktop) {
    return (
      <DesktopEventDetailsDialog
        open={showEventDetailsDialog}
        onClose={handleClose}
        onExited={onExited}
        group={selectedGroup}
        subtitle={[periodLabel, serverName].filter(Boolean).join(" · ")}
        events={events}
        eventTypes={eventTypes}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        singleDay={singleDay}
      />
    );
  }

  // Мобильный вид и Telegram — тёмный bottom sheet с карточками событий
  return (
    <EventDetailsSheet
      open={showEventDetailsDialog}
      onClose={handleClose}
      onExited={onExited}
      group={selectedGroup}
      events={events}
      singleDay={singleDay}
    />
  );
};

export default EventDetailsDialog;
